import React, { useContext, useEffect, useState } from 'react';
import * as S from './styled';
import { useSnackbar } from 'notistack';
import Form from '../..';
import { defaultBlurLogisticForm, defaultLogisticForm, } from '../../../../store/invoice/utils';
import { ExportContext } from '../../../../provider/ExportProvider';
import { maskFieldDate, unmaskFieldDate } from '../utils';
import { useAbility } from '../../../Hooks/useAbility';

export default function FormLogisticUpdate() {
  const {
    logistics,
    getInvoice,
    setInvoice,
    updateLogistic,
    setModal,
    getFormMessage,
    validateForm,
    filterSelectCompanies,
    selectCompanies,
    setCompanyFilterField,
    filterLogistic,
    logisticGroupItems,
    logisticGroups,
    selectedGroupId,
  } = useContext(ExportContext)!.controllers.logistic;
    const { ability } = useAbility();
  
  const { enqueueSnackbar } = useSnackbar();

  const getForm = <T extends FlattenKeys<LogisticForm>>(field: T) =>
    getInvoice(`forms.logistics.update.${field}`);

  const setForm = <
    T extends FlattenKeys<LogisticForm>,
    V extends DeepType<InvoiceState, `forms.logistics.update.${T}`>,
  >(
    field: T,
    value: V,
  ) => setInvoice(`forms.logistics.update.${field}`, value);

  const blurField = (field: keyof LogisticForm) => () => {
    setInvoice(`blur.logistics.update.${field}`, true);
  };

  const clearForm = () => {
    const logistic = logistics.find(
      ({ id }) => getInvoice('forms.logistics.update').id === id,
    )!;
    setInvoice(`forms.logistics.update`, {
      id: logistic.id,
      quantityVolume: logistic.quantityVolume ?? 0,
      nrcInvoiceNumber: logistic.nrcInvoiceNumber ?? '',
      clientInvoiceNumber: logistic.clientInvoiceNumber ?? '',
      crt: logistic.crt ?? '',
      clientName: logistic.clientName ?? '',
      warehouseDispatchDate: logistic.warehouseDispatchDate ?? '',
      revenueDate: logistic.revenueDate ?? '',
      customsDate: logistic.customsDate ?? '',
      storageDate: logistic.storageDate ?? '',
      clientDate: logistic.clientDate ?? '',
      releaseDate: logistic.releaseDate ?? '',
      observation: logistic.observation ?? '',
      company: {
        id: logistic.company.id,
        name: logistic.company.name,
      },
      group: {
        id: logistic.group?.id ?? 0,
        number: logistic.group?.number ?? 0,
        name: logistic.group?.name ?? ''
      }
    });
    setInvoice(`blur.logistics.update`, defaultBlurLogisticForm());
  };

  const messages = getFormMessage('logistics.update');

  const [loading, setLoading] = React.useState(false);
  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    validateForm('logistics.update')
      .then(() => {
        setLoading(true);
        updateLogistic()
          .then(() => {
            enqueueSnackbar('Logística criada com sucesso', {
              variant: 'success',
            });
            setInvoice('forms.logistics.update', { id: 0, ...defaultLogisticForm() });
            setModal('single.updateLogistic', false);
            filterLogistic();
          })
          .catch(() => {
            enqueueSnackbar('Houve uma falha ao criar a logística', {
              variant: 'error',
            });
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        console.log({ messages })
        enqueueSnackbar('Verifique a validade dos campos', {
          variant: 'error',
        });
      });
  };

  const [companyLoading, setCompanyLoading] = useState(false);
  const filterCompanies = () => {
    setCompanyLoading(true);
    filterSelectCompanies().finally(() => {
      setCompanyLoading(false);
    });
  };

  const group = getForm('group')

  useEffect(() => {
    if (!selectedGroupId && (!group.id && logisticGroups.length > 0))
      setForm('group', logisticGroups[0]);
  }, [logisticGroups, selectedGroupId]);

  return (
    <S.Form
      style={{
        maxHeight: '80vh',
      }}
      onSubmit={onSubmit}
    >
      <S.Body>
        <S.Grid>
          <Form.Input.Dropdown
            label="Companhia"
            items={selectCompanies.map(({ id, name }) => ({ id, name }))}
            value={getForm('company.name')}
            onChange={(e) => {
              setForm('company.name', e.target.value);
              setCompanyFilterField('name')(e);
            }}
            onDebouncedChange={filterCompanies}
            error={messages.company}
            onBlur={blurField('company')}
            hasClearItem
            onFocus={() => {
              setCompanyFilterField('name')({ target: { value: '' } });
            }}
            onSelected={({ id, name }) => {
              setForm('company', +id ? { id: +id, name: `${name}` } : { id: 0, name: '' });
            }}
            loading={companyLoading}
          // disabled={ability?.cannot('update', 'Invoice', 'company')}
          />

          <Form.Input
            label="Cliente"
            value={getForm('clientName')}
            onChange={(e) => setForm('clientName', e.target.value)}
            error={messages.clientName}
            onBlur={blurField('clientName')}
          />

          <Form.Input
            label="Quantidade Volume"
            value={getForm('quantityVolume')}
            onChange={(e) => setForm('quantityVolume', +e.target.value || 0)}
            error={messages.quantityVolume}
            onBlur={blurField('quantityVolume')}
          />

          <Form.Input
            label="Nota NRC"
            value={getForm('nrcInvoiceNumber')}
            onChange={(e) => setForm('nrcInvoiceNumber', e.target.value)}
            error={messages.nrcInvoiceNumber}
            onBlur={blurField('nrcInvoiceNumber')}
          />
          <Form.Input
            label="Nota Cliente"
            value={getForm('clientInvoiceNumber')}
            onChange={(e) => setForm('clientInvoiceNumber', e.target.value)}
            error={messages.clientInvoiceNumber}
            onBlur={blurField('clientInvoiceNumber')}
          />
          <Form.Input
            label="CRT"
            value={getForm('crt')}
            onChange={(e) => setForm('crt', e.target.value)}
            error={messages.crt}
            onBlur={blurField('crt')}
          />
          <Form.Input.DatePicker
            label="Data Lançamento"
            value={maskFieldDate(getForm('releaseDate'))}
            onChange={(e) => setForm('releaseDate', unmaskFieldDate(e.target.value))}
            error={messages.releaseDate}
            onBlur={blurField('releaseDate')}
          />
          <Form.Input.DatePicker
            label="Data Entreposto / Despacho"
            value={maskFieldDate(getForm('warehouseDispatchDate'))}
            onChange={(e) => setForm('warehouseDispatchDate', unmaskFieldDate(e.target.value))}
            error={messages.warehouseDispatchDate}
            onBlur={blurField('warehouseDispatchDate')}
          />
          <Form.Input.DatePicker
            label="Data Receita"
            value={maskFieldDate(getForm('revenueDate'))}
            onChange={(e) => setForm('revenueDate', unmaskFieldDate(e.target.value))}
            error={messages.revenueDate}
            onBlur={blurField('revenueDate')}
          />
          <Form.Input.DatePicker
            label="Data Aduana"
            value={maskFieldDate(getForm('customsDate'))}
            onChange={(e) => setForm('customsDate', unmaskFieldDate(e.target.value))}
            error={messages.customsDate}
            onBlur={blurField('customsDate')}
          />
          <Form.Input.DatePicker
            label="Data Armazenadora"
            value={maskFieldDate(getForm('storageDate'))}
            onChange={(e) => setForm('storageDate', unmaskFieldDate(e.target.value))}
            error={messages.storageDate}
            onBlur={blurField('storageDate')}
          />
          <Form.Input.DatePicker
            label="Data Cliente"
            value={maskFieldDate(getForm('clientDate'))}
            onChange={(e) => setForm('clientDate', unmaskFieldDate(e.target.value))}
            error={messages.clientDate}
            onBlur={blurField('clientDate')}
          />
          <Form.Input
            label="Observação"
            value={getForm('observation')}
            onChange={(e) => setForm('observation', e.target.value)}
            error={messages.observation}
            onBlur={blurField('observation')}
          />
          {ability?.can('update', 'Invoice', 'groupId') && !selectedGroupId && (logisticGroupItems.length > 1) ? (
            <Form.Input.Dropdown
              label="Grupo"
              items={logisticGroupItems}
              value={
                logisticGroupItems.find(({ id }) => id === getForm('group')?.id)?.name ??
                ''
              }
              error={messages.group}
              onBlur={blurField('group')}
              onSelected={(item) => {
                setForm('group', item.data);
              }}
              disabled={logisticGroupItems.length === 1}
            />
          ) : null}
        </S.Grid>
      </S.Body>
      <S.Actions>
        <Form.Button type="button" onClick={clearForm}>
          Limpar
        </Form.Button>
        <Form.Button loading={loading}>Atualizar</Form.Button>
      </S.Actions>
    </S.Form>
  );
}
